import React, { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import moment from "moment"
import { ArrowLeft, Edit, Trash2, Mail, Phone } from "lucide-react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import NavigationLink from "~/library/NavigationLink"
import AccordionSection from "~/library/UIkit/AccordionSection"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { capitalize } from "~/utils/helpers"
import { apiWorkspaceAccounts, apiWorkspaceDeals, apiWorkspaceContacts } from "~/routes"
import type Account from "~/types/Account"
import type Deal from "~/types/Deal"
import type Contact from "~/types/Contact"
import ResourceNotes from "~/library/ResourceNotes"
import CatchDropdown from "~/library/CatchDropdown"
import ConfirmationModal from "~/library/ConfirmationModal"
import { useToast } from "~/utils/Toast"
import TasksManager from "~/views/Workspace/Tasks/TasksManager"
import ActivityFeed from "~/views/Workspace/Activities/ActivityFeed"

const ACCOUNT_DELETE_WARNING =
  "This action cannot be undone. Deleting this account will also delete its deals, associated payments, and notes. Contacts and tasks attached to this account or its deals will not be deleted, but they will be unassigned."

const statusBadge = (status?: string | null) => {
  if (!status) {
    return <span className="badge badge-lg bg-secondary">Unknown</span>
  }

  const normalized = status.toLowerCase()

  if (normalized === "active") {
    return <span className="badge badge-lg bg-success">Active</span>
  }

  if (normalized === "past") {
    return <span className="badge badge-lg bg-secondary">Past</span>
  }

  if (normalized === "prospect") {
    return <span className="badge badge-lg bg-warning">Prospect</span>
  }

  return <span className="badge badge-lg bg-light text-dark">{capitalize(status)}</span>
}

const formatMoney = (value?: number | string | null) => {
  if (value == null) return "$0.00"
  const numValue = typeof value === "string" ? parseFloat(value) : value
  return `$${numValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const dealTypeBadge = (dealType?: string) => {
  if (!dealType) return null

  const typeColors: Record<string, string> = {
    sponsorship: "bg-primary",
    group_booking: "bg-info",
    season_tickets: "bg-success",
    suites: "bg-warning",
    corporate_event: "bg-danger",
  }

  const color = typeColors[dealType] || "bg-secondary"

  return (
    <span className={`badge ${color} font-11 me-1`}>{capitalize(dealType.replace(/_/g, " "))}</span>
  )
}

const AccountView: React.FC = () => {
  const { venueSlug } = getAppConfig()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const [activeTab, setActiveTab] = useState<"deals" | "contacts" | "tasks" | "activity">("deals")
  const [deals, setDeals] = useState<Deal[]>([])
  const [dealsLoading, setDealsLoading] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>({})
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)

  const endpoint = apiWorkspaceAccounts.show.path({ venue_slug: venueSlug, id: id! })
  const { data: account, isLoading } = useFetchResource<{ account: Account }, "account">(
    endpoint,
    "account",
    {}
  )

  useEffect(() => {
    if (account?.id) {
      fetchDeals()
      fetchContacts()
    }
  }, [account?.id])

  const fetchDeals = async () => {
    if (!account?.id) return

    setDealsLoading(true)
    try {
      const dealsEndpoint = apiWorkspaceDeals.index.path({ venue_slug: venueSlug })
      const url = `${dealsEndpoint}?q[account_id_eq]=${account.id}`

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
        },
      })

      const data = await response.json()
      setDeals(data.deals || [])
    } catch (error) {
      console.error("Error fetching deals:", error)
    } finally {
      setDealsLoading(false)
    }
  }

  const fetchContacts = async () => {
    if (!account?.id) return

    setContactsLoading(true)
    try {
      const contactsEndpoint = apiWorkspaceContacts.index.path({ venue_slug: venueSlug })
      const url = `${contactsEndpoint}?q[account_id_eq]=${account.id}`

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
        },
      })

      const data = await response.json()
      setContacts(data.contacts || [])
    } catch (error) {
      console.error("Error fetching contacts:", error)
    } finally {
      setContactsLoading(false)
    }
  }

  const dealsByStage = useMemo(() => {
    if (!deals) return {}

    const grouped: Record<string, Deal[]> = {}
    deals.forEach((deal) => {
      const stageId = deal.dealStage?.id || "no_stage"

      if (!grouped[stageId]) {
        grouped[stageId] = []
      }
      grouped[stageId].push(deal)
    })

    return grouped
  }, [deals])

  const stageTotals = useMemo(() => {
    const totals: Record<string, number> = {}

    Object.entries(dealsByStage).forEach(([stageId, stageDeals]) => {
      totals[stageId] = stageDeals.reduce((sum, deal) => {
        const value = typeof deal.value === "string" ? parseFloat(deal.value) : deal.value
        return sum + (value || 0)
      }, 0)
    })

    return totals
  }, [dealsByStage])

  const toggleStage = (stageId: string) => {
    setExpandedStages((prev) => ({
      ...prev,
      [stageId]: !prev[stageId],
    }))
  }

  const handleDeleteDeal = async (dealPrefixId: string, dealName: string) => {
    if (!confirm(`Are you sure you want to delete "${dealName}"? This action cannot be undone.`)) {
      return
    }

    try {
      const url = apiWorkspaceDeals.destroy.path({
        venue_slug: venueSlug,
        id: dealPrefixId,
      })

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })

      if (response.ok) {
        toast("Deal deleted successfully", "success")
        fetchDeals()
      } else {
        toast("Error deleting deal", "danger")
      }
    } catch (error) {
      console.error("Error:", error)
      toast("Error deleting deal", "danger")
    }
  }

  const handleDeleteContact = async (contactPrefixId: string, contactName: string) => {
    if (
      !confirm(`Are you sure you want to delete "${contactName}"? This action cannot be undone.`)
    ) {
      return
    }

    try {
      const url = apiWorkspaceContacts.destroy.path({
        venue_slug: venueSlug,
        id: contactPrefixId,
      })

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })

      if (response.ok) {
        toast("Contact deleted successfully", "success")
        fetchContacts()
      } else {
        toast("Error deleting contact", "danger")
      }
    } catch (error) {
      console.error("Error:", error)
      toast("Error deleting contact", "danger")
    }
  }

  const handleDeleteAccount = async () => {
    if (!account?.prefixId) return

    try {
      const url = apiWorkspaceAccounts.destroy.path({
        venue_slug: venueSlug,
        id: account.prefixId,
      })

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })

      if (response.ok) {
        toast("Account deleted successfully", "success")
        setIsDeletingAccount(false)
        navigate("/accounts")
      } else {
        toast("Error deleting account", "danger")
      }
    } catch (error) {
      console.error("Error:", error)
      toast("Error deleting account", "danger")
    }
  }

  if (isLoading || !account) {
    return <LoadingWrapper isLoading />
  }

  return (
    <LoadingWrapper isLoading={false}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate("/accounts")}
              aria-label="Back to Accounts"
              title="Back to Accounts"
            >
              <ArrowLeft />
            </button>
            <span>{account.name}</span>
          </div>
        }
      >
        <CatchDropdown
          label="Actions"
          className="ms-2"
          items={[
            {
              label: "Edit Account",
              to: `/accounts/${account?.prefixId}/edit`,
            },
            {
              label: "Delete Account",
              className: "text-danger",
              onClick: () => setIsDeletingAccount(true),
            },
          ]}
        />
      </PageTitle>

      <div className="row mb-3">
        <div className="col-12">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn ${activeTab === "deals" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveTab("deals")}
            >
              Deals
            </button>
            <button
              type="button"
              className={`btn ${activeTab === "contacts" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveTab("contacts")}
            >
              Contacts
            </button>
            <button
              type="button"
              className={`btn ${activeTab === "tasks" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveTab("tasks")}
            >
              Tasks
            </button>
            <button
              type="button"
              className={`btn ${activeTab === "activity" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setActiveTab("activity")}
            >
              Activity
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-8">
          {activeTab === "deals" && (
            <LoadingWrapper isLoading={dealsLoading}>
              {Object.keys(dealsByStage).length > 0 ? (
                Object.entries(dealsByStage).map(([stageId, stageDeals]) => {
                  const stageName = stageDeals[0]?.dealStage?.name || "No Stage"
                  const isExpanded = expandedStages[stageId] !== false

                  return (
                    <CatchCard key={stageId} className="mb-3" bodyClassName="p-2">
                      <AccordionSection
                        title={
                          <div className="d-flex justify-content-between align-items-center w-100 me-3">
                            <span>{stageName}</span>
                            <span className="badge bg-primary">
                              {stageDeals.length} deals · {formatMoney(stageTotals[stageId])}
                            </span>
                          </div>
                        }
                        isExpanded={isExpanded}
                        onToggle={() => toggleStage(stageId)}
                      >
                        <div className="mt-2">
                          {stageDeals.map((deal) => (
                            <div key={deal.id} className="border-bottom py-2 px-2">
                              <div className="row align-items-center">
                                <div className="col-6">
                                  <NavigationLink type="deals" to={`/deals/${deal.prefixId}`}>
                                    <span className="fw-semibold">{deal.name}</span>
                                  </NavigationLink>
                                  <div className="mt-1">
                                    {dealTypeBadge(deal.dealType.name)}
                                    {deal.probability && (
                                      <span className="badge bg-secondary font-11">
                                        {deal.probability}%
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="text-success fw-bold">
                                    {formatMoney(deal.value)}
                                  </div>
                                  {deal.expected_close_date && (
                                    <div className="text-muted font-12">
                                      {moment(deal.expected_close_date).format("MMM DD, YYYY")}
                                    </div>
                                  )}
                                </div>
                                <div className="col-3 text-end">
                                  <button
                                    className="btn btn-sm btn-link p-1"
                                    onClick={() => navigate(`/deals/${deal.prefixId}/edit`)}
                                    title="Edit Deal"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-link text-danger p-1"
                                    onClick={() => handleDeleteDeal(deal.prefixId, deal.name)}
                                    title="Delete Deal"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionSection>
                    </CatchCard>
                  )
                })
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">No deals associated with this account</p>
                </div>
              )}
            </LoadingWrapper>
          )}

          {activeTab === "contacts" && (
            <LoadingWrapper isLoading={contactsLoading}>
              {contacts.length > 0 ? (
                <CatchCard>
                  <div className="table-responsive">
                    <table className="table table-centered table-nowrap mb-0 ">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Category</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((contact) => (
                          <tr key={contact.id}>
                            <td>
                              <NavigationLink type="contacts" to={`/contacts/${contact.prefixId}`}>
                                <span className="fw-semibold">
                                  {contact.firstName} {contact.lastName}
                                </span>
                              </NavigationLink>
                            </td>
                            <td>{contact.role || "—"}</td>
                            <td>
                              {contact.email ? (
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="text-decoration-none"
                                >
                                  <Mail size={14} className="me-1" />
                                  {contact.email}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              {contact.phone ? (
                                <a href={`tel:${contact.phone}`} className="text-decoration-none">
                                  <Phone size={14} className="me-1" />
                                  {contact.phone}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              {contact.contactCategory ? (
                                <span className="badge bg-info">
                                  {contact.contactCategory.name}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-link p-1"
                                onClick={() => navigate(`/contacts/${contact.prefixId}/edit`)}
                                title="Edit Contact"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="btn btn-sm btn-link text-danger p-1"
                                onClick={() =>
                                  handleDeleteContact(
                                    contact.prefixId,
                                    `${contact.firstName} ${contact.lastName}`
                                  )
                                }
                                title="Delete Contact"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CatchCard>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">No contacts associated with this account</p>
                </div>
              )}
            </LoadingWrapper>
          )}

          {activeTab === "tasks" && <TasksManager accountId={account.prefixId} />}

          {activeTab === "activity" && (
            <CatchCard title="Activity" className="mt-3">
              <ActivityFeed itemType="Account" itemId={account.id} />
            </CatchCard>
          )}
        </div>

        <div className="col-4">
          <CatchCard title="Account Details">
            <div className="mb-3">
              <p className="text-muted fw-bold font-12 text-uppercase mb-1">Status</p>
              <div>{statusBadge(account.status)}</div>
            </div>

            <div className="mb-3">
              <p className="text-muted fw-bold font-12 text-uppercase mb-1">Type</p>
              <div className="font-14">{account.accountType?.name || "—"}</div>
            </div>

            <div className="mb-3">
              <p className="text-muted fw-bold font-12 text-uppercase mb-1">Industry</p>
              <div className="font-14">{account.industry || "—"}</div>
            </div>

            <div className="mb-3">
              <p className="text-muted fw-bold font-12 text-uppercase mb-1">Address</p>
              <div className="font-14">{account.address || "—"}</div>
            </div>

            <div className="mb-3">
              <p className="text-muted fw-bold font-12 text-uppercase mb-1">Website</p>
              <div className="font-14">
                {account.website ? (
                  <a href={account.website} target="_blank" rel="noreferrer">
                    {account.website}
                  </a>
                ) : (
                  "—"
                )}
              </div>
            </div>

            <hr />

            <div className="mb-3">
              <p className="text-muted fw-bold font-12 text-uppercase mb-1">Historical Spend</p>
              <div className="font-14 fw-bold">{formatMoney(account.historicalSpend)}</div>
            </div>

            <div className="mb-3">
              <p className="text-muted fw-bold font-12 text-uppercase mb-1">Lifetime Value</p>
              <div className="font-14 fw-bold">{formatMoney(account.lifetimeValue)}</div>
            </div>
          </CatchCard>

          <CatchCard title="Notes" className="mt-3">
            <ResourceNotes resourceType="account" resourceId={account?.prefixId} />
          </CatchCard>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeletingAccount}
        onClose={() => setIsDeletingAccount(false)}
        onConfirm={handleDeleteAccount}
        title={`Delete Account "${account.name}"?`}
        description={ACCOUNT_DELETE_WARNING}
        confirmButtonVariant="danger"
      />
    </LoadingWrapper>
  )
}

export default AccountView
